import { api, type AudienceResponse } from '../lib/api';
import { useResource } from '../lib/useResource';
import { formatNumber } from '../lib/dataState';
import { formatInstant } from '../lib/format';

/**
 * Audiência e experimentos.
 *
 * Duas regras que esta tela existe para não quebrar:
 *
 * 1. **Contagem observada e contagem estimada nunca se misturam** (AT-102). Um
 *    total único somando as duas daria ao dono um número que mistura o que foi
 *    medido com o que foi extrapolado — e ele decidiria como se tudo tivesse a
 *    mesma qualidade.
 *
 * 2. **Experimento que não isola aparece como não-isolante** (AT-041), com as
 *    variáveis extras listadas. Sem isso, a conclusão dele seria lida como
 *    qualquer outra.
 */

const TIER_LABEL: Record<string, string> = {
  unknown: 'Desconhecido',
  low: 'Baixo',
  medium: 'Médio',
  high: 'Alto',
  strategic: 'Estratégico',
};

const EXPERIMENT_STATE_LABEL: Record<string, string> = {
  designed: 'Desenhado',
  running: 'Em execução',
  concluded: 'Concluído',
  returned_not_isolating: 'Devolvido: não isola',
  abandoned: 'Abandonado',
};

const STRENGTH_LABEL: Record<string, string> = {
  weak_hypothesis: 'Hipótese fraca',
  supported: 'Apoiada',
  strong: 'Forte',
  confounded: 'Confundida',
  inconclusive: 'Inconclusiva',
};

export function Audience() {
  const resource = useResource<AudienceResponse>(() => api.audience(), []);

  if (resource.kind === 'loading') {
    return (
      <div className="text-sm" aria-busy="true">
        <h1 className="text-2xl font-bold">Audiência e crescimento</h1>
        <p className="mt-2 text-ink-muted">Carregando…</p>
      </div>
    );
  }

  if (resource.kind === 'error') {
    return (
      <div className="max-w-3xl space-y-4 text-sm">
        <h1 className="text-2xl font-bold">Audiência e crescimento</h1>
        <div className="mt-4 rounded border border-danger-border bg-danger-surface p-4">
          <p className="font-medium">Não foi possível carregar a audiência.</p>
          <p className="mt-1">{resource.message}</p>
          <p className="mt-2 text-xs text-ink-faint">
            Nenhuma coorte aparece com zero: zero e &quot;não sabemos&quot; são coisas diferentes.
            Código: {resource.code}
          </p>
        </div>
      </div>
    );
  }

  const { data } = resource;

  return (
    <div className="space-y-4 text-sm">
      <header>
        <h1 className="text-2xl font-bold">Audiência e crescimento</h1>
        <p className="mt-1 text-ink-muted">
          {formatNumber(data.cohorts.length)} coorte(s) e {formatNumber(data.experiments.length)}{' '}
          experimento(s).
        </p>
      </header>

      <section className="mt-6" aria-labelledby="coortes">
        <h2 id="coortes" className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
          Coortes
        </h2>
        {data.cohorts.length === 0 ? (
          <p className="mt-3 rounded-[11px] border border-line bg-surface-sunken p-4 text-ink-muted">
            Nenhuma coorte registrada. Coorte nasce de demografia que o provedor devolveu, e
            demografia ausente não vira coorte fabricada.
          </p>
        ) : (
          <ul className="mt-3 space-y-3">
            {data.cohorts.map((c) => (
              <li
                key={c.cohort_id}
                className="rounded-card border border-line bg-card p-4 shadow-card"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-medium">{c.name}</span>
                  <span className="text-xs uppercase tracking-wide text-ink-muted">
                    {TIER_LABEL[c.value_tier] ?? c.value_tier}
                  </span>
                </div>
                <p className="mt-1 text-xs text-ink-muted">{c.definition}</p>

                {/*
                  Os dois números aparecem SEPARADOS e rotulados. Um "total"
                  somando os dois esconderia qual parte foi medida.
                */}
                <dl className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <dt className="text-ink-muted">Contagem observada</dt>
                    <dd>
                      {c.observed_count === null ? (
                        <span className="text-ink-faint">o provedor não devolveu (não é zero)</span>
                      ) : (
                        <strong>{formatNumber(c.observed_count)}</strong>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-ink-muted">Contagem estimada</dt>
                    <dd>
                      {c.estimated_count === null ? (
                        <span className="text-ink-faint">nenhuma estimativa</span>
                      ) : (
                        <>
                          <strong>{formatNumber(c.estimated_count)}</strong>
                          <span className="text-ink-faint">
                            {' '}
                            ({c.estimation_method ?? 'método não declarado'}
                            {c.estimation_confidence === null
                              ? ''
                              : `, confiança ${c.estimation_confidence}`}
                            )
                          </span>
                        </>
                      )}
                    </dd>
                  </div>
                </dl>

                <p className="mt-2 text-xs text-ink-faint">
                  {c.source} · capturada em {formatInstant(c.captured_at)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-8" aria-labelledby="experimentos">
        <h2
          id="experimentos"
          className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
        >
          Experimentos
        </h2>
        {data.experiments.length === 0 ? (
          <p className="mt-3 rounded-[11px] border border-line bg-surface-sunken p-4 text-ink-muted">
            Nenhum experimento registrado.
          </p>
        ) : (
          <ul className="mt-3 space-y-3">
            {data.experiments.map((e) => {
              const extras = e.changed_variables.filter((v) => v !== e.isolated_variable);
              return (
                <li
                  key={e.experiment_id}
                  className={`rounded-card border border-line bg-card p-4 shadow-card ${
                    e.isolation_valid ? '' : 'border-l-4 border-l-danger bg-danger-surface/50'
                  }`}
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-medium">{e.name}</span>
                    <span className="text-xs text-ink-muted">
                      {EXPERIMENT_STATE_LABEL[e.state] ?? e.state}
                    </span>
                  </div>
                  <p className="mt-1">{e.hypothesis}</p>

                  <p className="mt-2 text-xs text-ink-muted">
                    Isola <strong>{e.isolated_variable}</strong> · julga por{' '}
                    <strong>{e.primary_metric}</strong> (objetivo {e.assigned_objective}) · amostra
                    mínima {formatNumber(e.minimum_sample_size)}
                  </p>

                  {!e.isolation_valid || extras.length > 0 ? (
                    <p className="mt-2 text-xs text-danger-ink">
                      Não isola: além de {e.isolated_variable}, mudaram {extras.join(', ')}. O
                      resultado não se atribui a nenhuma delas.
                      {e.isolation_note ? ` ${e.isolation_note}` : ''}
                    </p>
                  ) : null}

                  {e.conclusion ? (
                    <p className="mt-2 text-xs">
                      <strong>
                        {STRENGTH_LABEL[e.conclusion_strength ?? ''] ?? 'Sem força declarada'}:
                      </strong>{' '}
                      {e.conclusion}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
